import {Component, HostListener, signal} from '@angular/core';
import {MatIcon} from '@angular/material/icon';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {ApiService} from '../../../shared/services/api';
import {LoaderService} from '../../../shared/services/loader';
import {finalize} from 'rxjs';
import {User, UserResponse} from '../../../shared/types/customers.model';
import {getBaseUrl} from '../../../shared/utils/image-utils';
import {SnackBarService} from '../../../shared/services/snackbar';
import {FormsModule} from '@angular/forms';
import {DialogService} from '../../../shared/services/dialog';


@Component({
  selector: 'app-user-details',
  imports: [
    MatIcon,
    CommonModule,
    RouterLink,
    FormsModule
  ],
  templateUrl: './user-details.html',
  styleUrl: './user-details.css',
})
export class UserDetails {
  user = signal<User | null>(null);
  // User information
  userInfo = {
    lastSession: 'Today' // This might need to come from API in future
  };

  // Track current image index for each vehicle
  currentImageIndices: { [key: number]: number } = {};
  zoomedStates: { [key: number]: boolean } = {};
  zoomLevels: { [key: number]: number } = {};
  editableVehicles: { [key: number]: any } = {};

  // Edit mode tracking for BIN fields
  // editingBin: { [key: string]: boolean } = {};
  // tempBinValues: { [key: string]: string } = {};

  // Edit mode for entire vehicle
  editModeMap: { [key: string]: boolean } = {};

  editedVehicles: { [key: string]: {
    vendor: string;
    model: string;
    vin: string;
    bin: string;
    vehicleNo: string;
  }} = {};

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private loader: LoaderService,
    private snackbar: SnackBarService,
    private dialogService: DialogService
  ) {}

  ngOnInit() {
    const userId = this.route.snapshot.paramMap.get('id')!;
    this.loadUser(userId);
  }

  loadUser(userId: string) {
    this.loader.start();
    this.apiService.getUserDetails(userId)
      .pipe(finalize(() => this.loader.stop()))
      .subscribe({
        next: (res: UserResponse) => {
          console.log(res);
          this.user.set(res.data);
          // Initialize indices for each vehicle
          if (res.data.vehicles) {
            res.data.vehicles.forEach((vehicle, index) => {
              this.currentImageIndices[index] = 0;
              this.zoomedStates[index] = false;
              this.zoomLevels[index] = 1;
              this.editableVehicles[index] = { ...vehicle };
            });
          }
        },
        error: (err) => {
          console.error('Failed to get users', err);
        }
      });
  }

  hasUnsavedChanges(vehicleIndex: number): boolean {
    const original = this.user()?.vehicles?.[vehicleIndex];
    const edited = this.editableVehicles[vehicleIndex];

    if (!original || !edited) return false;

    return JSON.stringify(original) !== JSON.stringify(edited);
  }

  approveVehicle(vehicleId: string, vehicleIndex: number): void {
    if (this.hasUnsavedChanges(vehicleIndex)) {
      this.snackbar.error('Please save vehicle changes before approving');
      return;
    }
    this.loader.start();
    this.apiService.approveVehicle(vehicleId)
      .pipe(finalize(() => this.loader.stop()))
      .subscribe({
        next: () => {
          this.snackbar.success('Vehicle approved successfully');
          const userId = this.user()?.id;
          if (userId) {
            this.loadUser(userId);
          }
        },
        error: (err) => {
          console.error('Failed to approved vehicle', err);
          this.snackbar.error('Failed to approved vehicle');
        }
      });

  }
  rejectVehicle(vehicleId: string, vehicleIndex: number): void {
    if (this.hasUnsavedChanges(vehicleIndex)) {
      this.snackbar.error('Please save vehicle changes before rejecting');
      return;
    }
    this.loader.start();
    this.apiService.rejectVehicle(vehicleId)
      .pipe(finalize(() => this.loader.stop()))
      .subscribe({
        next: () => {
          this.snackbar.success('Vehicle reject successfully');
          const userId = this.user()?.id;
          if (userId) {
            this.loadUser(userId);
          }
        },
        error: (err) => {
          console.error('Failed to reject vehicle', err);
          this.snackbar.error('Failed to reject vehicle');
        }
      });

  }

  // Navigate to previous image for a specific vehicle
  previousImage(vehicleIndex: number): void {
    const vehicles = this.user()?.vehicles;
    if (!vehicles || !vehicles[vehicleIndex]) return;

    const currentIndex = this.currentImageIndices[vehicleIndex];
    const imagesLength = vehicles[vehicleIndex].proofImages.length;

    if (currentIndex > 0) {
      this.currentImageIndices[vehicleIndex]--;
    } else {
      this.currentImageIndices[vehicleIndex] = imagesLength - 1;
    }
  }

  // Navigate to next image for a specific vehicle
  nextImage(vehicleIndex: number): void {
    const vehicles = this.user()?.vehicles;
    if (!vehicles || !vehicles[vehicleIndex]) return;

    const currentIndex = this.currentImageIndices[vehicleIndex];
    const imagesLength = vehicles[vehicleIndex].proofImages.length;

    if (currentIndex < imagesLength - 1) {
      this.currentImageIndices[vehicleIndex]++;
    } else {
      this.currentImageIndices[vehicleIndex] = 0;
    }
  }

  // Get current image URL for a specific vehicle
  getCurrentImage(vehicleIndex: number): string {
    const FALLBACK_IMAGE = 'assets/images/mask-group.png';

    const vehicles = this.user()?.vehicles;
    if (!vehicles || vehicleIndex < 0 || vehicleIndex >= vehicles.length) {
      return FALLBACK_IMAGE;
    }

    const images = vehicles[vehicleIndex]?.proofImages;
    if (!images || images.length === 0) {
      return FALLBACK_IMAGE;
    }

    const imageIndex = this.currentImageIndices?.[vehicleIndex] ?? 0;

    const selectedImage = images[imageIndex];
    if (!selectedImage?.url) {
      return FALLBACK_IMAGE;
    }
    return `${getBaseUrl()}${selectedImage.url}`;
  }


  // Toggle zoom state for a specific vehicle
  toggleZoom(vehicleIndex: number): void {
    const isCurrentlyZoomed = this.zoomedStates[vehicleIndex];

    // Close any other zoomed image first
    for (const key of Object.keys(this.zoomedStates)) {
      const idx = parseInt(key);
      this.zoomedStates[idx] = false;
      this.zoomLevels[idx] = 1;
    }

    if (!isCurrentlyZoomed) {
      this.zoomedStates[vehicleIndex] = true;
      this.zoomLevels[vehicleIndex] = 1.5;
    }
  }

  // Check if a specific vehicle image is zoomed
  isZoomed(vehicleIndex: number): boolean {
    return this.zoomedStates[vehicleIndex] || false;
  }

  // Get zoom level for a specific vehicle
  getZoomLevel(vehicleIndex: number): number {
    return this.zoomLevels[vehicleIndex] || 1;
  }

  // Handle mouse wheel events with Ctrl key
  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    // Find which vehicle is currently zoomed
    const zoomedVehicleIndex = Object.keys(this.zoomedStates).find(
      key => this.zoomedStates[parseInt(key)]
    );

    if (zoomedVehicleIndex !== undefined && event.ctrlKey) {
      event.preventDefault(); // Prevent browser zoom
      const index = parseInt(zoomedVehicleIndex);

      const delta = event.deltaY > 0 ? -0.1 : 0.1;
      const newZoomLevel = this.zoomLevels[index] + delta;

      // Limit zoom level between 0.5 and 3
      this.zoomLevels[index] = Math.max(0.5, Math.min(3, newZoomLevel));
    }
  }
// Status enum
getStatusClass(status: string): string {
    switch (status) {
      case UserStatus.APPROVED:
        return 'approved-wrapper';
      case UserStatus.PENDING:
        return 'pending-wrapper';
      case UserStatus.REJECTED:
        return 'rejected-wrapper';
      default:
        return '';
    }
  }// Get status badge clas


  // Update vehicle field
  updateVehicleField(vehicleId: string, field: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    if (this.editedVehicles[vehicleId]) {
      (this.editedVehicles[vehicleId] as any)[field] = target.value;
    }
  }

  // Toggle edit mode for all vehicles
  toggleEditMode(vehicleId: string): void {
    if (this.editModeMap[vehicleId]) {
      this.saveVehicle(vehicleId);
    } else {
      this.editModeMap[vehicleId] = true;

      const vehicle = this.user()?.vehicles?.find(v => v.id === vehicleId);
      if (vehicle) {
        this.editedVehicles[vehicleId] = {
          vendor: vehicle.vendor || '',
          model: vehicle.model || '',
          vin: vehicle.vin || '',
          bin: vehicle.bin || '',
          vehicleNo: vehicle.vehicleNo || ''
        };
      }
    }
  }


  // Cancel edit mode
  cancelEditMode(vehicleId: string): void {
    this.editModeMap[vehicleId] = false;

    const vehicle = this.user()?.vehicles?.find(v => v.id === vehicleId);
    if (vehicle) {
      this.editedVehicles[vehicleId] = {
        vendor: vehicle.vendor,
        model: vehicle.model,
        vin: vehicle.vin,
        bin: vehicle.bin,
        vehicleNo: vehicle.vehicleNo
      };
    }
  }


  // Save all changes
  saveVehicle(vehicleId: string): void {
    const data = this.editedVehicles[vehicleId];

    this.loader.start();

    this.apiService.updateVehicle(vehicleId, data).subscribe({
      next: () => {
        this.loader.stop();
        this.snackbar.success('Vehicle updated');

        this.loader.stop();
        this.snackbar.success('Vehicle updated');

        this.editModeMap[vehicleId] = false;

        // 🔥 Reload fresh data from backend
        this.loadUser(this.user()?.id!);
      },
      error: () => {
        this.loader.stop();
        this.snackbar.error('Update failed');
      }
    });
  }




  protected readonly UserStatus = UserStatus;
}
enum UserStatus {
  APPROVED = 'APPROVED',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED'
}
